import type { JSX } from "react";
import type { FreeformBlock as FreeformBlockModel, FreeformNode } from "@shared/types";
import type { BlockRendererProps } from "./registry";

/**
 * Freeform-block renderer (Feature 3, Lane 2): renders the safe primitive tree
 * (heading / paragraph / list / callout / columns / button) as themed native
 * elements. No raw HTML is ever rendered — text fields are plain strings, so this
 * is injection-free by construction. A `button` node sends its prompt via the
 * host bridge.
 */
export function FreeformBlockView({ block, onSendPrompt }: BlockRendererProps): JSX.Element {
  const ff = block as FreeformBlockModel;
  return <div className="freeform-block">{ff.nodes.map((n, i) => renderNode(n, i, onSendPrompt))}</div>;
}

/** Render one freeform node; recurses for columns. */
function renderNode(
  node: FreeformNode,
  key: number,
  onSendPrompt: (text: string) => void
): JSX.Element | null {
  switch (node.type) {
    case "heading": {
      const cls = `freeform-h freeform-h${node.level}`;
      if (node.level === 1) return <div key={key} className={cls} role="heading" aria-level={1}>{node.text}</div>;
      if (node.level === 2) return <div key={key} className={cls} role="heading" aria-level={2}>{node.text}</div>;
      return <div key={key} className={cls} role="heading" aria-level={3}>{node.text}</div>;
    }
    case "paragraph":
      return <p key={key} className="freeform-p">{node.text}</p>;
    case "list":
      return node.ordered === true ? (
        <ol key={key} className="freeform-list">{node.items.map((it, j) => <li key={j}>{it}</li>)}</ol>
      ) : (
        <ul key={key} className="freeform-list">{node.items.map((it, j) => <li key={j}>{it}</li>)}</ul>
      );
    case "callout":
      return (
        <div key={key} className="freeform-callout" data-tone={node.tone ?? "info"}>
          {node.title !== undefined && node.title.length > 0 && (
            <span className="freeform-callout-title">{node.title}</span>
          )}
          {node.text}
        </div>
      );
    case "columns":
      return (
        <div key={key} className="freeform-columns">
          {node.columns.map((col, ci) => (
            <div key={ci} className="freeform-column">
              {col.map((c, cj) => renderNode(c, cj, onSendPrompt))}
            </div>
          ))}
        </div>
      );
    case "button":
      return (
        <button
          key={key}
          type="button"
          className="freeform-button"
          onClick={() => onSendPrompt(node.prompt)}
        >
          {node.label}
        </button>
      );
    default:
      return null;
  }
}
